/**
 * A quina URL porta cada idioma des d'una ruta donada.
 *
 * Viu aquí i no al LanguageSelector perquè és lògica pura i ha de tenir
 * test: el component importa react-router-dom i el resolutor de jest no
 * en sap resoldre el subcamí, així que un test al costat del component
 * ni arrencava.
 *
 * El valor que importa és el null: l'arquitectura del Sprint 132 diu que
 * la URL decideix l'idioma, o sigui que un idioma sense URL en aquesta
 * ruta vol dir que la pàgina NO existeix en aquell idioma. Qui ho crida
 * n'ha de fer cas i no pintar aquell idioma.
 */
import { ARTICLES } from '../pages/blog/articles';
import { resolvePath, pathFor } from '../config/routeSlugs';

// Sprint 119.71 — Rutes que tenen versió canònica per idioma (subcarpeta).
// Sincronitzat amb HREFLANG_PATHS de SEO.js.
const LANG_SUBFOLDER_PATHS = new Set([
  '/',
  '/ficha-tecnica',
  '/initiative',
  '/instaladores',
  '/solicitar-sello',
  '/pool-check',
  '/calculadoras',
]);


/**
 * Sprint 119.83 — Construeix la URL de destí en canviar d'idioma.
 * Regles:
 *  1. Blog article (/blog/{slug} o /{lang}/blog/{slug}): busca l'article
 *     pel slug actual i naveg al slug del nou idioma. Si no existeix
 *     l'article, cau al blog index del nou idioma.
 *  2. Blog index (/blog o /{lang}/blog): naveg al blog index del nou idioma.
 *  3. Ruta amb subcarpeta idioma prefixada (/en/foo, /fr/foo...): extreu
 *     el path net i afegeix el prefix del nou idioma si és una ruta amb
 *     subcarpeta canònica, altrament neutre.
 *  4. Ruta neutra: si és una ruta amb subcarpeta canònica i el nou idioma
 *     no és ES, afegeix prefix; ES es queda neutre (canònica ES = sense prefix).
 *  5. Fallback: canvia només i18n sense navegar (rutes no cobertes).
 */
export function buildLangUrl(currentPath, newLang) {
  // Normalitza: sense trailing slash excepte "/"
  const path = currentPath !== '/' && currentPath.endsWith('/')
    ? currentPath.slice(0, -1)
    : currentPath;

  // Detecta prefix d'idioma actual: /en/foo -> ['en', '/foo']
  const langPrefixMatch = path.match(/^\/(es|en|fr|de|it|nl)(\/.*)?$/);
  const currentLang = langPrefixMatch ? langPrefixMatch[1] : 'es';
  const pathNoLang = langPrefixMatch
    ? (langPrefixMatch[2] || '/')
    : path;

  // ─── Blog article ───
  const blogArticleMatch = pathNoLang.match(/^\/blog\/(.+)$/);
  if (blogArticleMatch) {
    const currentSlug = blogArticleMatch[1];
    const article = ARTICLES.find(a =>
      Object.values(a.slugs).includes(currentSlug)
    );
    if (article && article.slugs[newLang]) {
      const newSlug = article.slugs[newLang];
      return newLang === 'es'
        ? `/blog/${newSlug}`
        : `/${newLang}/blog/${newSlug}`;
    }
    // Article no trobat o no té traducció -> blog index del nou idioma
    return newLang === 'es' ? '/blog' : `/${newLang}/blog`;
  }

  // ─── Blog index ───
  if (pathNoLang === '/blog') {
    return newLang === 'es' ? '/blog' : `/${newLang}/blog`;
  }

  // ─── Ruta amb subcarpeta canònica (/, /ficha-tecnica, /initiative, ...) ───
  if (LANG_SUBFOLDER_PATHS.has(pathNoLang)) {
    if (pathNoLang === '/') {
      return newLang === 'es' ? '/' : `/${newLang}`;
    }
    return newLang === 'es' ? pathNoLang : `/${newLang}${pathNoLang}`;
  }

  // ─── Calculadores amb slug per idioma (Sprint 161) ───
  // Fins ara queien al fallback: el selector canviava l'idioma en memòria i
  // la URL es quedava, així que de /tools/pool-volume-calculator no es podia
  // passar a /calculadora-volumen-piscina. Si no hi ha versió, null.
  const hit = resolvePath(path);
  if (hit) return pathFor(hit.key, newLang);

  // ─── Fallback: rutes no cobertes ───
  // null = aquesta ruta NO existeix en aquell idioma.
  //
  // Abans aquí es canviava i18n en memòria sense navegar, i no servia de
  // res: l'idioma surt de la URL (i18n/index.js, detector urlPath amb
  // caches: []) i LanguageSync el torna a alinear amb la ruta. El clic
  // no feia res a les 36 rutes públiques que cauen aquí ni a les dues
  // seccions del mapa de slugs que només tenen castellà
  // (/calculadora-base-piscina-elevada i /configurador-cubierta-piscina).
  //
  // Qui crida això ara en fa cas: els idiomes que donen null no es
  // pinten, en comptes de pintar-se i no fer res.
  return null;
}
